import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, MapPin, MessageCircle, CheckCircle, ArrowRight, AlertTriangle } from "lucide-react";
import { areas, services, getBusinessConfig } from "@/lib/site-config";
import { CTABlock } from "@/components/CTABlock";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { ProcessSteps } from "@/components/ProcessSteps";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { AreaPageCTAs } from "@/components/AreaPageCTAs";
import { PrimaryCTA } from "@/components/PrimaryCTA";

// Unique local+service intros for every area/service combo
const areaServiceContent: Record<string, Record<string, {
  intro: string;
  localBenefit: string;
  faqs: { q: string; a: string }[];
}>> = {
  poplar: {
    mot: {
      intro: "Living or working in Poplar? Our MOT testing station is just minutes away via East India Dock Road. Drop your car off and walk to the DLR while we test it.",
      localBenefit: "Quick walk from Poplar DLR — have your MOT done during a lunch break",
      faqs: [
        { q: "Can I get an MOT near Poplar High Street?", a: "Yes — we're just a few minutes' drive from Poplar High Street. Book ahead and we'll have you in and out quickly." },
        { q: "Do you offer free retests for Poplar customers?", a: "We offer free retests for all customers if we carry out the repairs. It's not area-specific — everyone gets the same fair deal." },
      ],
    },
    servicing: {
      intro: "Keep your car running smoothly with regular servicing near Poplar. We handle interim, full and major services for all makes and models — with your service book stamped.",
      localBenefit: "Convenient drop-off for Poplar residents — easy access from the A12",
      faqs: [
        { q: "How often should I service my car in Poplar's stop-start traffic?", a: "Urban driving with lots of short trips can be harder on your engine. We'd recommend sticking to the manufacturer schedule or a 12-month/12,000-mile interval — whichever comes first." },
      ],
    },
    diagnostics: {
      intro: "Warning light on? Don't ignore it. Our diagnostic equipment covers all major manufacturers and we're just minutes from Poplar. We find the root cause — not just clear the code.",
      localBenefit: "Fast diagnostic checks — most done within 30 minutes",
      faqs: [
        { q: "Can you diagnose my car near Poplar?", a: "Yes — bring it in or call ahead. We'll connect our diagnostic tools and give you a clear explanation of what's going on." },
      ],
    },
    "mechanical-repairs": {
      intro: "From brakes and clutches to suspension and gearbox work, we handle all mechanical repairs for Poplar drivers. Quality parts, honest quotes, fast turnaround.",
      localBenefit: "Most repairs completed same-day for local Poplar customers",
      faqs: [
        { q: "How quickly can you fix my brakes near Poplar?", a: "Brake pad and disc replacements are usually done within a few hours. Call us and we'll book you in as soon as possible." },
      ],
    },
    bodywork: {
      intro: "Scratches, dents and bumper damage are common on Poplar's busy streets. We offer professional bodywork repairs with colour matching — restoring your car to its best.",
      localBenefit: "Professional colour matching for seamless results",
      faqs: [
        { q: "Can you fix car park dents near Poplar?", a: "Yes — minor dents and scratches can often be repaired same-day. Bring the car in or send us photos via WhatsApp for a quick quote." },
      ],
    },
  },
  "canary-wharf": {
    mot: {
      intro: "Work in Canary Wharf? Drop your car off first thing and collect it after work with a fresh MOT. We're a short drive from the estate via Aspen Way.",
      localBenefit: "Drop off before work, collect after — no disruption to your day",
      faqs: [
        { q: "Can I have my MOT done while I'm at work in Canary Wharf?", a: "Absolutely. Many of our customers drop their car in the morning and pick it up after work. We'll call you with the results." },
      ],
    },
    servicing: {
      intro: "Professional car servicing convenient for Canary Wharf workers and residents. All makes including premium brands — BMW, Mercedes, Audi and more.",
      localBenefit: "Popular with Canary Wharf professionals who want hassle-free servicing",
      faqs: [
        { q: "Do you service premium cars for Canary Wharf customers?", a: "Yes — we regularly service and repair BMW, Mercedes, Audi, Porsche and other premium brands. Same quality, without dealer prices." },
      ],
    },
    diagnostics: {
      intro: "Dashboard warning light bothering you on your commute? Our professional diagnostic equipment pinpoints the exact fault — saving you time and money on guesswork.",
      localBenefit: "Professional diagnostics covering all major brands",
      faqs: [
        { q: "Can you diagnose my BMW/Mercedes near Canary Wharf?", a: "Yes. Our diagnostic tools cover all major European, Japanese and Korean manufacturers. We'll find the issue and explain it clearly." },
      ],
    },
    "mechanical-repairs": {
      intro: "Expert mechanical repairs close to Canary Wharf. Brakes, suspension, clutch, gearbox — we handle it all with clear communication and fair pricing.",
      localBenefit: "Clear quotes and updates — designed for busy professionals",
      faqs: [
        { q: "How do I arrange a repair from Canary Wharf?", a: "Call us or send a WhatsApp. We'll arrange a time for drop-off and keep you updated throughout. Most repairs are same-day." },
      ],
    },
    bodywork: {
      intro: "Premium bodywork repairs convenient for Canary Wharf. From underground car park scratches to bumper scuffs — we repair and colour-match to perfection.",
      localBenefit: "Quality finish on premium vehicles — Bentley, Mercedes, BMW and more",
      faqs: [
        { q: "Can you fix a scratch on my executive car?", a: "Yes — we regularly repair bodywork on premium vehicles. Professional colour matching ensures a seamless finish. Send us photos for a quick quote." },
      ],
    },
  },
  "isle-of-dogs": {
    mot: {
      intro: "Need an MOT on the Isle of Dogs? We're nearby via Manchester Road — no need to drive far for a thorough, honest test.",
      localBenefit: "Local testing station — avoid long trips across London",
      faqs: [{ q: "Where's the nearest MOT station to Isle of Dogs?", a: "We're one of the closest authorised MOT stations. A quick drive via Manchester Road or Westferry Road." }],
    },
    servicing: {
      intro: "Regular servicing for Isle of Dogs residents. We'll keep your car reliable and stamp your service book — whether it's a daily commuter or family SUV.",
      localBenefit: "Service book stamped to protect your warranty",
      faqs: [{ q: "Will servicing with you void my warranty?", a: "No. Under UK law you can have your car serviced at any independent garage without affecting your warranty, as long as correct parts and procedures are used." }],
    },
    diagnostics: {
      intro: "Professional fault finding for Isle of Dogs drivers. We investigate the cause, not just the symptom — saving you money on unnecessary part replacements.",
      localBenefit: "Root-cause diagnostics — not just code clearing",
      faqs: [{ q: "My engine light keeps coming back — can you help?", a: "Yes. If a light returns after being cleared elsewhere, it usually means the underlying fault wasn't fixed. We dig deeper to find and resolve the actual cause." }],
    },
    "mechanical-repairs": {
      intro: "All mechanical repairs covered for Isle of Dogs drivers. Brakes, clutch, suspension, exhaust — quality parts and honest pricing, no upselling.",
      localBenefit: "Honest quotes — we only fix what needs fixing",
      faqs: [{ q: "Can you replace a clutch near Isle of Dogs?", a: "Yes. Clutch replacements typically take a day. We'll give you a clear quote before starting work." }],
    },
    bodywork: {
      intro: "Professional bodywork for Isle of Dogs residents. Tight parking and narrow streets mean dents happen — we repair them to a high standard with proper colour matching.",
      localBenefit: "Minor dent and scratch repairs often done same-day",
      faqs: [{ q: "How much does bumper repair cost?", a: "It depends on the damage. Send us photos via WhatsApp for a quick estimate, or bring the car in for a proper assessment." }],
    },
  },
  limehouse: {
    mot: {
      intro: "MOT testing for Limehouse drivers. Easy access from Commercial Road — get your vehicle tested quickly with honest results and no hidden charges.",
      localBenefit: "Quick access from Commercial Road and the A13",
      faqs: [{ q: "How do I get to you from Limehouse?", a: "We're a short drive via Commercial Road or Burdett Road. Also walkable from Limehouse DLR." }],
    },
    servicing: {
      intro: "Car servicing near Limehouse. Interim, full and major services for all makes — keeping your vehicle reliable in busy East London traffic.",
      localBenefit: "All service levels available — interim, full and major",
      faqs: [{ q: "What's the difference between an interim and full service?", a: "An interim covers essentials like oil, filters and brakes. A full service adds additional checks and replacements. We'll advise which is right for your car." }],
    },
    diagnostics: {
      intro: "Diagnostic fault finding near Limehouse. We use professional-grade equipment to identify exactly what's wrong — no guesswork, no unnecessary parts swaps.",
      localBenefit: "Experienced technicians who understand what codes actually mean",
      faqs: [{ q: "Can you clear my engine warning light near Limehouse?", a: "We can — but only after we've found and resolved the underlying issue. Clearing a light without fixing the fault is a waste of your money." }],
    },
    "mechanical-repairs": {
      intro: "Mechanical repairs for Limehouse drivers. From brake pad replacements to major engine work — we handle it all with clear quotes and fast turnaround.",
      localBenefit: "Clear communication throughout every repair",
      faqs: [{ q: "Do you fix suspension near Limehouse?", a: "Yes — springs, shock absorbers, bushings, drop links. We'll inspect the issue and quote before starting work." }],
    },
    bodywork: {
      intro: "Bodywork repairs near Limehouse. Scuffs, dents, and paint damage repaired to a professional standard with proper colour matching.",
      localBenefit: "Competitive pricing for quality bodywork",
      faqs: [{ q: "Can you repair rust near Limehouse?", a: "Yes — we offer rust treatment and prevention as well as cosmetic repairs. Best to catch it early before it spreads." }],
    },
  },
  docklands: {
    mot: {
      intro: "MOT testing for Docklands drivers across Royal Docks, Silvertown and Canning Town. Authorised station with fast turnaround and honest results.",
      localBenefit: "Centrally located for the wider Docklands area",
      faqs: [{ q: "Do you test vans from Docklands businesses?", a: "Yes — we test Class 4 (cars and light vans) and Class 7 (goods vehicles up to 3,500kg). Call to confirm your vehicle." }],
    },
    servicing: {
      intro: "Professional servicing for Docklands vehicles. Whether it's a personal car or a business van, we'll keep it running smoothly with proper parts and a stamped service book.",
      localBenefit: "Fleet and business vehicle servicing available",
      faqs: [{ q: "Can you service our company vehicles?", a: "Yes — we work with several local businesses on regular servicing and maintenance. Call us to discuss fleet arrangements." }],
    },
    diagnostics: {
      intro: "Advanced diagnostics for Docklands drivers. Professional equipment, experienced technicians, and clear explanations — so you understand exactly what's happening with your vehicle.",
      localBenefit: "Professional-grade scanners covering all major manufacturers",
      faqs: [{ q: "Can you diagnose electrical faults?", a: "Yes — our diagnostic equipment covers engine management, ABS, airbags, traction control and other electronic systems." }],
    },
    "mechanical-repairs": {
      intro: "Full mechanical repair service for Docklands drivers. Brakes, clutch, gearbox, suspension, exhaust — quality work with clear pricing.",
      localBenefit: "Quality OE-equivalent parts as standard",
      faqs: [{ q: "Do you offer warranties on repairs?", a: "Yes — all repairs come with a parts and labour warranty. Ask us for details when you book in." }],
    },
    bodywork: {
      intro: "Bodywork and paint repairs for Docklands vehicles. From minor cosmetic damage to more significant panel work — professional results at fair prices.",
      localBenefit: "From small dents to full panel repairs",
      faqs: [{ q: "Do you handle insurance bodywork claims?", a: "We can provide detailed assessments and quotes to support your insurance claim. Speak to us for details." }],
    },
  },
  "tower-hamlets": {
    mot: {
      intro: "MOT testing for Tower Hamlets drivers. Whether you're in Bethnal Green, Mile End, Stepney, Bow or Whitechapel — we're easy to reach from the A11 and Mile End Road.",
      localBenefit: "Serving the whole borough — Bethnal Green to Bow",
      faqs: [{ q: "Where's the nearest MOT station in Tower Hamlets?", a: "We're based in E14, easy to reach from most of Tower Hamlets via Mile End Road or Commercial Road." }],
    },
    servicing: {
      intro: "Car servicing across Tower Hamlets. All makes and models, all service levels — we keep local vehicles running reliably with honest advice and fair pricing.",
      localBenefit: "Strong local reputation for honest, quality servicing",
      faqs: [{ q: "Do you service older cars?", a: "Yes — we work on vehicles of all ages. Older cars often need more attention and we're experienced in keeping them roadworthy and reliable." }],
    },
    diagnostics: {
      intro: "Diagnostic fault finding for Tower Hamlets drivers. We investigate the actual cause of your problem — not just the symptom — so the fix lasts.",
      localBenefit: "We investigate the cause, not just the symptom",
      faqs: [{ q: "My car keeps stalling — can you diagnose it?", a: "Yes. Intermittent issues like stalling can have many causes — fuel, ignition, sensors. Our diagnostic approach finds the root cause so the fix sticks." }],
    },
    "mechanical-repairs": {
      intro: "Trusted mechanical repairs across Tower Hamlets. Brakes, suspension, clutch, exhaust, steering — honest pricing and experienced mechanics.",
      localBenefit: "Experienced mechanics who've seen it all",
      faqs: [{ q: "Can you repair my exhaust near Tower Hamlets?", a: "Yes — we handle exhaust repairs and replacements. Bring it in and we'll assess whether it needs a repair or a full replacement." }],
    },
    bodywork: {
      intro: "Bodywork repairs for Tower Hamlets drivers. Scratches from tight parking, bumper damage, dents — all repaired with professional colour matching.",
      localBenefit: "Professional finish at competitive prices",
      faqs: [{ q: "Can you fix a keyed car near Tower Hamlets?", a: "Yes — deep scratches can be repaired and repainted with professional colour matching. Bring the car in or send photos for a quote." }],
    },
  },
  whitechapel: {
    mot: { intro: "Need an MOT near Whitechapel? We're a DVSA-authorised MOT station a short drive from Whitechapel Road. Drop the car off and walk back or take the Elizabeth line — we'll call when it's ready.", localBenefit: "Quick to reach via Commercial Road or Whitechapel Road", faqs: [{ q: "How quickly can I book an MOT near Whitechapel?", a: "Most weeks we can fit you in within a few days. Call ahead and we'll find the earliest slot." }] },
    servicing: { intro: "Local car servicing for Whitechapel drivers — interim, full and major services, all stamped in your service book. Quality parts, no upsell.", localBenefit: "Service book stamped — warranty protected", faqs: [{ q: "Can you service my car without voiding the warranty?", a: "Yes — using OE-equivalent parts and following the manufacturer schedule keeps your warranty intact under Block Exemption rules." }] },
    diagnostics: { intro: "Warning light on around Whitechapel? Our diagnostic tools cover all major manufacturers. We find the underlying fault — not just clear the code — so the fix actually sticks.", localBenefit: "Real diagnosis, not guesswork", faqs: [{ q: "How long does a diagnostic check take?", a: "Most diagnostics are done within 30–60 minutes. Intermittent faults can take longer — we'll quote first." }] },
    bodywork: { intro: "Bodywork repairs for Whitechapel drivers. Scratches, dents and bumper scuffs repaired with professional colour matching at fair prices.", localBenefit: "In-house bodywork with proper colour matching", faqs: [{ q: "Can you repair a scratch without respraying the whole panel?", a: "Often yes — local repair techniques can blend into the existing paint without a full panel respray." }] },
  },
  "brick-lane": {
    mot: { intro: "Quick, honest MOTs near Brick Lane. Drop the car off, grab a coffee on Brick Lane, and we'll have you on the road again the same day where possible.", localBenefit: "Easy walking access to Brick Lane while you wait", faqs: [{ q: "Is your MOT competitively priced for Brick Lane customers?", a: "We work to standard DVSA MOT fees with no hidden extras." }] },
    servicing: { intro: "Independent car servicing close to Brick Lane. Practical advice, quality parts and clear quotes — no dealer prices, no surprise add-ons.", localBenefit: "Independent — no upselling", faqs: [{ q: "What's the difference between interim and full?", a: "Interim is a check and oil change every 6 months / 6,000 miles; a full is more thorough and annual. We'll advise based on mileage." }] },
    diagnostics: { intro: "Diagnostic checks for E1/E2 drivers. We connect to your car, read fault codes properly, and explain what's actually going on in plain English.", localBenefit: "Plain-English explanations", faqs: [{ q: "My engine light keeps coming back — what's wrong?", a: "Recurring engine lights usually mean the underlying fault hasn't been fixed. We diagnose the root cause, not just reset the light." }] },
    bodywork: { intro: "Bodywork and paint repair near Brick Lane. From bumper scuffs to door dents — colour matched in-house and finished to a high standard.", localBenefit: "Colour-matched repairs in-house", faqs: [{ q: "Can you quote bodywork from photos?", a: "Yes — send photos via WhatsApp and we'll give you a guide price before you bring the car in." }] },
  },
  bow: {
    mot: { intro: "DVSA-authorised MOT testing close to Bow. Drop your car with us via the A11 — most MOTs done same day, with a free retest if we handle the repairs.", localBenefit: "Free retest when we do the repairs", faqs: [{ q: "Do you do MOTs on diesels from Bow?", a: "Yes — petrol, diesel and hybrid MOTs for cars and small commercial vehicles." }] },
    servicing: { intro: "Car servicing for Bow drivers — interim, full and major. All makes, all stamped in your service book.", localBenefit: "All makes, all stamped", faqs: [{ q: "Will servicing here affect my warranty?", a: "No — Block Exemption means you can service at any qualified independent and keep your warranty." }] },
    diagnostics: { intro: "Diagnostic fault finding for Bow drivers. We find the underlying fault behind warning lights and explain what needs doing — no scare tactics.", localBenefit: "No scare tactics — honest diagnostics", faqs: [{ q: "Can you reset the service light too?", a: "Yes — after work we'll reset relevant service and warning lights as part of the job." }] },
  },
  "mile-end": {
    mot: { intro: "MOT testing close to Mile End — a short drive via Mile End Road or Burdett Road. Drop the car and walk back home while we test it.", localBenefit: "Walk back to Mile End while we test", faqs: [{ q: "Can I get an MOT during the week from Mile End?", a: "Yes — weekday and Saturday slots available. Book ahead for the best times." }] },
    servicing: { intro: "Full car servicing for Mile End drivers. Quality oils and filters, all checks done properly, and a clear written report at the end.", localBenefit: "Clear written report at the end", faqs: [{ q: "How often should I service my car?", a: "Short urban trips can be tough on engines — we'd suggest a 12-month/12,000-mile interval, whichever comes first." }] },
  },
  "bethnal-green": {
    mot: { intro: "Honest MOT testing close to Bethnal Green. We test thoroughly, explain advisories properly, and don't fail cars for things that don't need it.", localBenefit: "Honest testing — no inventing faults", faqs: [{ q: "What's the difference between a fail and an advisory?", a: "An advisory is a wear item that should be monitored; a fail is a defect that makes the car unroadworthy. We'll explain each." }] },
    servicing: { intro: "Local car servicing for Bethnal Green drivers. Interim, full and major service options to suit your mileage and budget.", localBenefit: "Service options to suit your budget", faqs: [{ q: "Can you advise which service I actually need?", a: "Yes — tell us your mileage and last service date and we'll recommend the right level honestly." }] },
    diagnostics: { intro: "Diagnostics for Bethnal Green drivers. Warning lights, electrical issues, intermittent faults — we investigate with manufacturer-level tools.", localBenefit: "Manufacturer-level diagnostic tools", faqs: [{ q: "My ABS light is on — is it safe to drive?", a: "Often yes for short distances, but braking can be affected — bring it in for diagnosis as soon as you can." }] },
  },
  wapping: {
    mot: { intro: "Quick, honest MOTs for Wapping drivers. A short drive via The Highway — drop off and we'll call you when you're good to go.", localBenefit: "Very close to Wapping — minimal travel", faqs: [{ q: "Can I book an MOT online?", a: "Yes — use the booking form or call us and we'll confirm the slot." }] },
    servicing: { intro: "Local servicing for Wapping (E1W). All makes, all service intervals, quality parts and clear quotes upfront.", localBenefit: "Clear quotes upfront", faqs: [{ q: "Do you service hybrids?", a: "Yes — we service most hybrid models. Call us with your make and model to confirm." }] },
    bodywork: { intro: "Bodywork repairs close to Wapping. From parking dents to scratched panels — repaired in-house with proper colour matching.", localBenefit: "Proper colour matching, every time", faqs: [{ q: "How long do bodywork repairs take?", a: "Minor repairs are often done in 1–2 days. Larger panel work depends on parts and paint drying time." }] },
  },
};

export const Route = createFileRoute("/areas/$area/$service")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ params, loaderData }) => {
    const area = areas.find((a) => a.slug === params.area);
    const service = services.find((s) => s.slug === params.service);
    const areaName = area?.name ?? params.area;
    const serviceName = service?.name ?? params.service;
    const localCtx = areaServiceContent[params.area]?.[params.service];
    const jsonLd: Record<string, unknown>[] = [
      localBusinessSchema({
        ...complianceToBusinessOpts(loaderData?.compliance),
        serviceCoverage: [areaName, ...(complianceToBusinessOpts(loaderData?.compliance).serviceCoverage || [])],
      }),
    ];
    if (service) {
      const mergedFaqs = [...(service.faqs || []), ...(localCtx?.faqs || [])];
      if (mergedFaqs.length) jsonLd.push(faqSchema(mergedFaqs) as Record<string, unknown>);
      jsonLd.push(serviceSchema({
        name: `${serviceName} in ${areaName}`,
        serviceType: serviceName,
        description: `Professional ${serviceName.toLowerCase()} for drivers in ${areaName}, East London. ${localCtx?.intro || ""}`.trim(),
        areaServed: `${areaName}, London`,
        url: `https://woodseerautos.co.uk/areas/${params.area}/${params.service}`,
      }));
    }
    return seoHead({
      path: `/areas/${params.area}/${params.service}`,
      title: `${serviceName} in ${areaName} — Woodseer Autos`,
      description: `Professional ${serviceName.toLowerCase()} near ${areaName}, East London. Honest advice, fair pricing. Call Woodseer Autos: ${NAP.phone}.`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Areas", path: "/areas" },
        { name: areaName, path: `/areas/${params.area}` },
        { name: serviceName, path: `/areas/${params.area}/${params.service}` },
      ],
      jsonLd,
    });
  },
  component: AreaServicePage,
  notFoundComponent: () => (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground">Page Not Found</h1>
        <p className="mt-2 text-muted-foreground">We couldn't find that service area page.</p>
        <Link to="/areas" className="mt-4 inline-block text-primary hover:text-primary/80">View all areas →</Link>
      </div>
    </div>
  ),
});

function AreaServicePage() {
  const { area: areaSlug, service: serviceSlug } = Route.useParams();
  const area = areas.find((a) => a.slug === areaSlug);
  const service = services.find((s) => s.slug === serviceSlug);
  const biz = getBusinessConfig();

  if (!area || !service) {
    throw notFound();
  }

  const localContent = areaServiceContent[area.slug]?.[service.slug];
  const allFaqs = [
    ...service.faqs,
    ...(localContent?.faqs ?? []),
  ];

  return (
    <>
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 text-primary text-sm font-medium mb-4">
            <MapPin className="h-4 w-4" />
            {area.name}, East London
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight">
            {service.name} in {area.name}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {localContent?.intro ?? `Looking for ${service.name.toLowerCase()} near ${area.name}? Woodseer Autos offers professional ${service.name.toLowerCase()} for drivers across ${area.name} and the surrounding East London area. ${service.intro.split('.')[0]}.`}
          </p>
          {localContent && (
            <p className="mt-3 text-sm text-primary font-medium">✓ {localContent.localBenefit}</p>
          )}
          <div className="mt-8 flex justify-center">
            <PrimaryCTA location={`area_${area.slug}_${service.slug}_hero`} intent={`${service.name} quote in ${area.name}`} variant="light" />
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      <AreaPageCTAs
        areaSlug={area.slug}
        areaName={area.name}
        serviceSlug={service.slug}
        serviceName={service.name}
      />

      {/* What's included */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What's Included</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {service.included.map((item) => (
                <StaggerItem key={item}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Common issues */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Common Issues We See</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {service.commonIssues.map((issue) => (
                <StaggerItem key={issue}>
                  <div className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm text-muted-foreground bg-card/50">
                    <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {issue}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Why choose us */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Why {area.name} Drivers Choose Us</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {service.whyUs.map((reason) => (
                <StaggerItem key={reason}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {reason}
                  </div>
                </StaggerItem>
              ))}
              <StaggerItem>
                <div className="flex items-start gap-3 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  Conveniently located for {area.name} residents and workers
                </div>
              </StaggerItem>
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <ProcessSteps steps={service.process} />
      </AnimatedSection>

      <AnimatedSection>
        <FAQAccordion faqs={allFaqs} title={`${service.name} in ${area.name} — FAQs`} />
      </AnimatedSection>

      {/* Cross-links */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl font-bold text-foreground text-center">Other Services in {area.name}</h2>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {services.filter((s) => s.slug !== service.slug).map((s) => (
                <Link
                  key={s.slug}
                  to="/areas/$area/$service"
                  params={{ area: area.slug, service: s.slug }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-border bg-background text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  {s.name}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
            <h3 className="mt-10 text-xl font-bold text-foreground text-center">{service.name} in Other Areas</h3>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {areas.filter((a) => a.slug !== area.slug).map((a) => (
                <Link
                  key={a.slug}
                  to="/areas/$area/$service"
                  params={{ area: a.slug, service: service.slug }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-border bg-background text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <CTABlock
        headline={`Book ${service.name} Near ${area.name}`}
        subtext={`Give us a call to arrange your ${service.name.toLowerCase()}. We're nearby, honest and ready to help.`}
      />
    </>
  );
}
